import { WorkspaceAction } from '@sales-app/context/workspace/workspace.actions'
import { useWorkspaceContext } from '@sales-app/context/workspace/workspace.hooks'
import { HomeFilters } from '@sales-app/context/workspace/workspace.type'
import { AgentQueries } from '@sales-app/graphql/queries/agent.queries'
import { BookingAgent } from '@sales-app/types/Agent'
import debounce from 'debounce-promise'
import { Form, Formik } from 'formik'
import React, { FC } from 'react'

import { InputSelect } from '@/components'
import { useUserContext } from '@/context'
import { AgentRoles } from '@/context/user/user.types'
import { graphQLClient } from '@/hooks/useGraphql'

type Props = {
  currentFilters: HomeFilters
}

export const AgentSelection: FC<Props> = ({ currentFilters }) => {
  const { agent } = currentFilters
  const { user } = useUserContext()
  const debouncedSearchUser = debounce(searchAgent, 1000, { leading: true })
  const isSupervisor = user?.agent?.role === AgentRoles.SUPERVISOR
  const isAssigned = !!agent?.id
  const [, WorkspaceDispatch] = useWorkspaceContext()

  const loadAgenOptions = (inputValue) => debouncedSearchUser(inputValue)

  const handleSubmit = async (values: { agentId: number }) => {
    if (values.agentId) {
      const agent = await getAgent(values.agentId)
      if (agent) {
        WorkspaceDispatch(WorkspaceAction.updateFilters({ agent }))
      }
    }
  }

  const handleOnClear = () => {
    if (isSupervisor) {
      WorkspaceDispatch(WorkspaceAction.updateFilters({ agent: null }))
    }
  }

  return (
    <Formik
      initialValues={{
        agentId: agent?.id,
      }}
      onSubmit={handleSubmit}
    >
      {({ submitForm }) => {
        return (
          <Form className=" flex w-1/2 flex-row flex-wrap">
            <InputSelect
              async={true}
              className="w-full"
              defaultOptions={false}
              disabled={!isSupervisor && isAssigned}
              id="assigneeId"
              loadOptions={loadAgenOptions}
              name="agentId"
              nullLabel="---"
              onChange={submitForm}
              onClear={handleOnClear}
              optionValue={agent ? { label: agent?.name || '', value: agent?.id } : undefined}
              overviewDropdown={true}
            />
          </Form>
        )
      }}
    </Formik>
  )
}

const searchAgent = (inputValue: string) => {
  return graphQLClient
    .request(AgentQueries.getSearchAgent(), {
      searchString: inputValue,
    })
    .then((resRespons: AgentQueries.GET_SEARCH_AGENT.Response) => {
      if (resRespons.agents) {
        return resRespons.agents.map((agent) => ({ label: agent?.name || '', value: agent?.id }))
      } else {
        return []
      }
    })
}

const getAgent = async (id: number) => {
  const resRespons: AgentQueries.GET_AGENT.Response = await graphQLClient.request(AgentQueries.getAgentDetails(), {
    id: id,
  })
  if (resRespons.agent) {
    return { id: resRespons.agent.id, name: resRespons.agent.name || '' } as BookingAgent
  } else {
    return null
  }
}
